import React, { useEffect, useRef } from "react";
import { Route, Redirect, RouteProps, RouteComponentProps } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import {
  canAccessPortalPath,
  getAccessiblePortalPath,
  getUnauthorizedPortalMessage,
} from "utils/portal-access";
import {
  ProtectedRouteSection,
  ProtectedRouteContentStack,
  ProtectedRouteSkeletonLine,
  ProtectedRouteSkeletonLineSecondary,
  ProtectedRouteSkeletonField,
  ProtectedRouteSkeletonLineTertiary,
} from "components/atoms/protected-route/styles/protected-route.inline.styles";

let hasShownAccessRestoreToast = false;

interface ProtectedRouteProps extends RouteProps {
  component: React.ComponentType<RouteComponentProps>;
  path: string;
}
const GuardSkeleton: React.FC<{
  label: string;
}> = ({ label }) => (
  <ProtectedRouteSection>
    <ProtectedRouteContentStack aria-label={label} role="status">
      <ProtectedRouteSkeletonLine />
      <ProtectedRouteSkeletonLineSecondary />
      <ProtectedRouteSkeletonField />
      <ProtectedRouteSkeletonLineTertiary />
    </ProtectedRouteContentStack>
  </ProtectedRouteSection>
);
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ component: Component, ...rest }) => {
  const { isAuthenticated, isReady, portalAccess, setPortalAccess, setUserRole, userRole } =
    useAuth();
  const lastUnauthorizedPath = useRef<string | null>(null);
  const shouldResolvePortalAccess = isAuthenticated && (!userRole || !portalAccess);
  const {
    data: currentUser,
    isError,
    isLoading,
  } = useQuery(
    buildCurrentUserQueryOptions({
      enabled: shouldResolvePortalAccess,
      retry: false,
    }),
  );
  const effectivePortalAccess = portalAccess || currentUser?.portal_access || null;
  useEffect(() => {
    if (currentUser?.role) {
      setUserRole(currentUser.role);
    }
    if (currentUser?.portal_access) {
      setPortalAccess(currentUser.portal_access);
    }
  }, [currentUser, setPortalAccess, setUserRole]);
  useEffect(() => {
    if (!isAuthenticated || !shouldResolvePortalAccess) {
      hasShownAccessRestoreToast = false;
      return;
    }

    if (!isReady || isLoading || !isError) {
      return;
    }

    const message = "We couldn't restore your workspace details yet. Showing the default view.";

    if (!hasShownAccessRestoreToast) {
      hasShownAccessRestoreToast = true;
      toast.error(message);
    }
  }, [isAuthenticated, isError, isLoading, isReady, shouldResolvePortalAccess]);
  useEffect(() => {
    const targetPath =
      typeof rest.path === "string" && !rest.path.includes(":")
        ? rest.path
        : window.location.pathname;
    if (!isAuthenticated || !isReady || isLoading) {
      return;
    }
    if (!effectivePortalAccess) {
      return;
    }
    if (
      !canAccessPortalPath(targetPath, effectivePortalAccess) &&
      lastUnauthorizedPath.current !== targetPath
    ) {
      lastUnauthorizedPath.current = targetPath;
      toast.error(getUnauthorizedPortalMessage(targetPath));
    }
  }, [effectivePortalAccess, isAuthenticated, isLoading, isReady, rest.path]);
  return (
    <Route
      {...rest}
      render={(props) =>
        !isReady ? (
          <GuardSkeleton label="Preparing your workspace" />
        ) : !isAuthenticated ? (
          <Redirect
            to={{
              pathname: "/login",
              state: {
                from: props.location,
              },
            }}
          />
        ) : shouldResolvePortalAccess && isLoading ? (
          <GuardSkeleton label="Checking workspace access" />
        ) : canAccessPortalPath(props.location.pathname, effectivePortalAccess) ? (
          <Component {...props} />
        ) : (() => {
          const redirectPath = getAccessiblePortalPath(effectivePortalAccess);

          if (props.location.pathname === redirectPath) {
            return <Component {...props} />;
          }

          return (
            <Redirect
              to={{
                pathname: redirectPath,
                state: {
                  from: props.location,
                },
              }}
            />
          );
        })()
      }
    />
  );
};
export default ProtectedRoute;
